// PRD 라벨 출력 공통 모듈
//
// 출력물 정의는 코드가 아니라 DB에 데이터로 등록되어 있다.
// - sys_report : 출력물 마스터(cd_report, tp_report, cd_querys)
// - sys_query  : 쿼리 마스터(cd_query -> dc_query). dc_query의 바인딩 변수(:no_sn 등)가
//                곧 출력에 필요한 파라미터다.
// FE는 양식명과 파라미터만 만들어 fnPrintByReport에 넘기고,
// tp_report(WINDRV/CMDRAW)별 출력 경로는 fnPrintByReport 내부에서 분기한다.
//
// 양식명/공정코드를 코드에 하드코딩하지 말 것.
// 업체와 공정에 따라 값이 달라지므로 반드시 데이터(mas_item 설정)로만 판정한다.

import { fnPrintByReport } from "@shared/utils/CommonUtils";

// 라벨 종류
// - carton : Inner Box(카톤) 라벨. 실적 1건당 1장, 파라미터는 no_plt 하나
// - prd    : SN(실린지) 라벨. 대상 1건당 1장, 파라미터는 nm_item + no_sn
export type LabelKind = "carton" | "prd";

export interface LabelPrintJob {
  kind: LabelKind;
  report: string;
  // fnPrintByReport에 그대로 넘길 파라미터
  params: Record<string, any>;
  // 실제로 출력될 라벨 장수(버튼 문구/확인 메시지에 사용)
  count: number;
}

// 안내 문구용 라벨 종류 이름.
// 양식명(cd_report)이 아니라 종류 이름이므로 업체가 늘어도 그대로 쓸 수 있다.
const LABEL_KIND_NAME: Record<LabelKind, string> = {
  carton: "카톤 라벨",
  prd: "SN 라벨",
};

const toTrimmed = (value: any) => (value ?? "").toString().trim();

// mas_item에 설정된 양식으로 라벨 종류를 판정한다.
// 한 품목에 nm_carton_report와 nm_prd_report가 동시에 설정되는 경우는 없다.
export const resolveLabelKind = (row: any): LabelKind | null => {
  if (toTrimmed(row?.nm_carton_report)) return "carton";
  if (toTrimmed(row?.nm_prd_report)) return "prd";
  return null;
};

export const resolveLabelReport = (row: any) => {
  const kind = resolveLabelKind(row);
  if (kind === "carton") return toTrimmed(row?.nm_carton_report);
  if (kind === "prd") return toTrimmed(row?.nm_prd_report);
  return "";
};

// 카톤 라벨 쿼리는 파이프로 감싼 목록을 받아 여러 행으로 펼친다.
//   select ... from (select string_to_table(trim(both '|' from :no_plt), '|') as no_plt)
// 즉 "|CT001|CT002|" 형태로 넘기면 한 번의 호출로 여러 장이 출력된다.
export const buildNoPltParam = (noCtList: string[]) => {
  const values = Array.from(
    new Set((noCtList || []).map(toTrimmed).filter((v) => !!v))
  );
  if (values.length < 1) return "";
  return `|${values.join("|")}|`;
};

// SN(실린지) 라벨 쿼리는 단건이며 두 값만 사용한다.
//   select :nm_item as nm_item, :no_sn as no_sn
export const buildPrdLabelParams = (nmItem: any, noSn: any) => ({
  nm_item: toTrimmed(nmItem),
  no_sn: toTrimmed(noSn),
});

// 대상 행들을 라벨 종류/양식별로 묶어 출력 작업 목록을 만든다.
// 여러 실적을 함께 선택하면 양식이 섞일 수 있으므로 양식 기준으로 그룹핑한다.
export const buildLabelPrintJobs = (rows: any[]): LabelPrintJob[] => {
  const targets = Array.isArray(rows) ? rows : [];
  const jobs: LabelPrintJob[] = [];
  const cartonByReport = new Map<string, string[]>();

  targets.forEach((row: any) => {
    const kind = resolveLabelKind(row);
    const report = resolveLabelReport(row);
    if (!kind || !report) return;

    if (kind === "carton") {
      const noCt = toTrimmed(row?.no_ct);
      if (!noCt) return;
      // 같은 카톤에 여러 실적행이 달리므로 양식별로 모아 중복을 제거한다.
      const list = cartonByReport.get(report) || [];
      list.push(noCt);
      cartonByReport.set(report, list);
      return;
    }

    const noSn = toTrimmed(row?.no_sn);
    if (!noSn) return;
    // SN 라벨 쿼리는 단건(select :nm_item, :no_sn)이라 대상마다 한 번씩 호출한다.
    jobs.push({
      kind,
      report,
      params: buildPrdLabelParams(row?.nm_item, noSn),
      count: 1,
    });
  });

  cartonByReport.forEach((noCtList, report) => {
    const noPlt = buildNoPltParam(noCtList);
    if (!noPlt) return;
    jobs.push({
      kind: "carton",
      report,
      params: { no_plt: noPlt },
      // 중복 제거 후 남은 카톤 수가 곧 장수다.
      count: noPlt.split("|").filter((v) => !!v).length,
    });
  });

  return jobs;
};

// 출력 불가 사유를 판정한다. 출력 가능하면 null.
// fnPrintByReport가 던지는 "라벨양식 정보를 찾을 수 없습니다."는 어느 값이 문제인지
// 알려주지 않으므로, 화면에서 먼저 걸러 구분되는 안내를 준다.
export const findLabelPrintError = (rows: any[]) => {
  const targets = Array.isArray(rows) ? rows : [];
  if (targets.length < 1) return "재출력할 실적을 선택하세요.";

  const hasReport = targets.some((row: any) => !!resolveLabelKind(row));
  if (!hasReport) return "라벨양식이 설정되지 않은 품목입니다.";

  const jobs = buildLabelPrintJobs(targets);
  if (jobs.length < 1) return "출력할 라벨 정보가 없습니다.";

  return null;
};

// 출력 작업을 종류별로 집계해 "카톤 라벨 2장, SN 라벨 10장" 형태로 만든다.
// 카톤과 SN은 장수 산정 기준이 달라(카톤은 실적 여러 건이 1장) 합쳐서 보여주면 오해를 준다.
export const summarizeLabelPrintJobs = (jobs: LabelPrintJob[]) => {
  const countByKind = new Map<LabelKind, number>();
  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    countByKind.set(job.kind, (countByKind.get(job.kind) || 0) + job.count);
  });

  // 표시 순서를 고정해 문구가 호출마다 바뀌지 않게 한다.
  const parts: string[] = [];
  (["carton", "prd"] as LabelKind[]).forEach((kind) => {
    const count = countByKind.get(kind) || 0;
    if (count > 0) parts.push(`${LABEL_KIND_NAME[kind]} ${count}장`);
  });

  return parts.join(", ");
};

// 출력 작업을 순서대로 실행한다.
// 동시에 요청하면 라벨 순서가 섞이거나 출력 서비스가 요청을 흘리므로 순차 처리한다.
// 개별 실패가 나머지 출력을 막지 않도록 결과를 모아 반환한다.
export const runLabelPrintJobs = async (jobs: LabelPrintJob[]) => {
  const succeeded: LabelPrintJob[] = [];
  const failed: { job: LabelPrintJob; error: any }[] = [];

  for (const job of Array.isArray(jobs) ? jobs : []) {
    try {
      await fnPrintByReport(job.report, job.params);
      succeeded.push(job);
    } catch (error: any) {
      failed.push({ job, error });
    }
  }

  const printedCount = succeeded.reduce((sum, job) => sum + job.count, 0);
  return { succeeded, failed, printedCount };
};
